import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import {
  Button,
  Combobox,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
  type FieldControlProps,
} from "@h2j-data-team/ui";

import type { QlikField, SyncStatus } from "@repo/shared";
import { QlikExpressionEditor } from "@/components/qlik/qlik-expression-editor";
import {
  MeasureColorField,
  MeasureNumberFormatField,
} from "@/components/qlik/measure-format-fields";
import { pickerMessages } from "@/lib/ui-labels";
import type { VariableForm, DimensionForm, MeasureForm } from "../use-group-items-draft";
import { SYNC_TONE, statusKey } from "../sync-status";

type FieldErrors = Record<string, string> | undefined;

/**
 * The accessible name and state of a Qlik expression editor. Its textbox is a
 * contenteditable, which `Field`'s `<label for>` cannot name, so the label text and
 * the Field's description and error wiring go to the editor's own aria props.
 * They are spread because the props arrive with the editor's accessibility update
 * (feat/577-a-edicao); until both branches meet the editor ignores them.
 */
function editorAria(control: FieldControlProps, label: string) {
  return {
    ariaLabel: label,
    ariaLabelledBy: control["aria-labelledby"],
    ariaDescribedBy: control["aria-describedby"],
    ariaInvalid: control["aria-invalid"] === true,
  };
}

/** A field label with the muted "(optional)" suffix. */
function OptionalLabel({ label, optional }: { label: string; optional: string }) {
  return (
    <>
      {label} <span className="font-normal text-faint">{optional}</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Field combobox (Qlik field picker for dimensions)
// ---------------------------------------------------------------------------

/**
 * Picks a data-model field, or takes a typed expression: a search that matches no
 * field offers "use as typed", so the value can still be any Qlik expression.
 */
export function FieldCombobox({
  value,
  onChange,
  fields,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  fields: QlikField[] | undefined;
  /** Accessible name of the picker. */
  label: string;
}) {
  const { t } = useTranslation("sync-groups");

  return (
    <Combobox
      aria-label={label}
      className="font-mono"
      options={(fields ?? []).map((field) => ({ value: field.name, label: field.name }))}
      value={value || null}
      onValueChange={(next) => onChange(next ?? "")}
      onCreate={onChange}
      createLabel={(query) => t("fieldCombobox.useTyped", { query })}
      messages={pickerMessages({
        placeholder: t("fieldCombobox.placeholder"),
        searchPlaceholder: t("fieldCombobox.searchPlaceholder"),
        emptyLabel: t("fieldCombobox.noFields"),
      })}
    />
  );
}

// ---------------------------------------------------------------------------
// Variable form
// ---------------------------------------------------------------------------

export function GroupVariableForm({
  value,
  onChange,
  errors,
}: {
  value: VariableForm;
  onChange: (next: VariableForm) => void;
  errors?: FieldErrors;
}) {
  const { t } = useTranslation("sync-groups");
  return (
    <div className="flex flex-col gap-4">
      <Field label={t("createVariable.nameLabel")} error={errors?.name}>
        <Input
          className="font-mono"
          placeholder={t("createVariable.namePlaceholder")}
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </Field>

      <Field
        label={t("createVariable.definitionLabel")}
        hint={t("createVariable.definitionHint")}
        error={errors?.definition}
      >
        {(control) => (
          <QlikExpressionEditor
            id={control.id}
            {...editorAria(control, t("createVariable.definitionLabel"))}
            placeholder={t("createVariable.definitionPlaceholder")}
            value={value.definition}
            onChange={(val) => onChange({ ...value, definition: val })}
            minHeight="160px"
          />
        )}
      </Field>

      <Field
        label={
          <OptionalLabel
            label={t("createVariable.descriptionLabel")}
            optional={t("createVariable.descriptionOptional")}
          />
        }
      >
        <Textarea
          placeholder={t("createVariable.descriptionPlaceholder")}
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          rows={2}
        />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dimension form
// ---------------------------------------------------------------------------

export function GroupDimensionForm({
  value,
  onChange,
  errors,
  availableFields,
}: {
  value: DimensionForm;
  onChange: (next: DimensionForm) => void;
  errors?: FieldErrors;
  availableFields: QlikField[] | undefined;
}) {
  const { t } = useTranslation("sync-groups");

  const setFields = (fields: DimensionForm["fields"]) => onChange({ ...value, fields });

  return (
    <div className="flex flex-col gap-4">
      <Field label={t("createDimension.titleLabel")} error={errors?.title}>
        <Input
          placeholder={t("createDimension.titlePlaceholder")}
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
      </Field>

      <Field label={t("createDimension.typeLabel")}>
        {(control) => (
          <Select
            value={value.grouping}
            onValueChange={(v) => {
              const grouping = v as "N" | "H" | "C";
              // Single dimensions only keep one field.
              onChange({
                ...value,
                grouping,
                fields:
                  grouping === "N" ? [value.fields[0] ?? { def: "", label: "" }] : value.fields,
              });
            }}
          >
            <SelectTrigger {...control}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="N">{t("createDimension.typeSingle")}</SelectItem>
              <SelectItem value="H">{t("createDimension.typeDrillDown")}</SelectItem>
              <SelectItem value="C">{t("createDimension.typeCyclic")}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </Field>

      <Field group label={t("createDimension.fieldsLabel")} error={errors?.fieldDefs}>
        {(control) => (
          <div
            role="group"
            aria-labelledby={control["aria-labelledby"]}
            aria-describedby={control["aria-describedby"]}
            className="flex flex-col gap-2"
          >
            {value.fields.map((field, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <FieldCombobox
                    label={t("createDimension.fieldN", { n: index + 1 })}
                    value={field.def}
                    onChange={(v) => {
                      const next = [...value.fields];
                      next[index] = { ...next[index], def: v };
                      setFields(next);
                    }}
                    fields={availableFields}
                  />
                </div>
                {value.grouping !== "N" && (
                  <div className="min-w-0 flex-1">
                    <Input
                      value={field.label}
                      onChange={(e) => {
                        const next = [...value.fields];
                        next[index] = { ...next[index], label: e.target.value };
                        setFields(next);
                      }}
                      aria-label={t("createDimension.fieldLabelN", { n: index + 1 })}
                      placeholder={t("createDimension.fieldLabelPlaceholder")}
                    />
                  </div>
                )}
                {value.fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label={t("createDimension.removeField", { n: index + 1 })}
                    title={t("createDimension.removeField", { n: index + 1 })}
                    onClick={() => setFields(value.fields.filter((_, i) => i !== index))}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
            ))}
            {value.grouping !== "N" && (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFields([...value.fields, { def: "", label: "" }])}
                >
                  <Plus /> {t("createDimension.addField")}
                </Button>
              </div>
            )}
          </div>
        )}
      </Field>

      {value.grouping === "N" && (
        <Field label={t("createDimension.labelExpressionLabel")}>
          {(control) => (
            <QlikExpressionEditor
              id={control.id}
              {...editorAria(control, t("createDimension.labelExpressionLabel"))}
              value={value.labelExpression}
              onChange={(val) => onChange({ ...value, labelExpression: val })}
              placeholder={t("createDimension.labelExpressionPlaceholder")}
              minHeight="120px"
            />
          )}
        </Field>
      )}

      <Field
        label={
          <OptionalLabel
            label={t("createDimension.descriptionLabel")}
            optional={t("createDimension.descriptionOptional")}
          />
        }
      >
        <Textarea
          placeholder={t("createDimension.descriptionPlaceholder")}
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          rows={2}
        />
      </Field>

      <Field label={t("createDimension.tagsLabel")}>
        <Input
          placeholder={t("createDimension.tagsPlaceholder")}
          value={value.tagsText}
          onChange={(e) => onChange({ ...value, tagsText: e.target.value })}
        />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Measure form
// ---------------------------------------------------------------------------

export function GroupMeasureForm({
  value,
  onChange,
  errors,
}: {
  value: MeasureForm;
  onChange: (next: MeasureForm) => void;
  errors?: FieldErrors;
}) {
  const { t } = useTranslation("sync-groups");
  return (
    <div className="flex flex-col gap-4">
      <Field label={t("createMeasure.titleLabel")} error={errors?.title}>
        <Input
          placeholder={t("createMeasure.titlePlaceholder")}
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
      </Field>

      <Field
        label={t("createMeasure.expressionLabel")}
        hint={t("createMeasure.expressionHint")}
        error={errors?.expression}
      >
        {(control) => (
          <QlikExpressionEditor
            id={control.id}
            {...editorAria(control, t("createMeasure.expressionLabel"))}
            placeholder={t("createMeasure.expressionPlaceholder")}
            value={value.expression}
            onChange={(val) => onChange({ ...value, expression: val })}
            minHeight="160px"
          />
        )}
      </Field>

      <Field
        label={
          <OptionalLabel
            label={t("createMeasure.descriptionLabel")}
            optional={t("createMeasure.descriptionOptional")}
          />
        }
      >
        <Textarea
          placeholder={t("createMeasure.descriptionPlaceholder")}
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          rows={2}
        />
      </Field>

      <Field label={t("createMeasure.labelFieldLabel")}>
        <Input
          placeholder={t("createMeasure.labelPlaceholder")}
          value={value.label}
          onChange={(e) => onChange({ ...value, label: e.target.value })}
        />
      </Field>

      <div className="grid grid-cols-2 items-start gap-4">
        <MeasureNumberFormatField
          value={value.numberFormat}
          onChange={(v) => onChange({ ...value, numberFormat: v })}
          idPrefix="gm"
          label={t("createMeasure.numberFormatLabel")}
          customOptionLabel={t("createMeasure.numberFormatCustom")}
        />
        <MeasureColorField
          value={value.color}
          onChange={(v) => onChange({ ...value, color: v })}
          idPrefix="gm"
          label={t("createMeasure.colorLabel")}
          clearLabel={t("createMeasure.colorClear")}
        />
      </div>

      <Field label={t("createMeasure.tagsLabel")}>
        <Input
          placeholder={t("createMeasure.tagsPlaceholder")}
          value={value.tagsText}
          onChange={(e) => onChange({ ...value, tagsText: e.target.value })}
        />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-app sync status surface (detail panel)
// ---------------------------------------------------------------------------

export interface AppSyncEntry {
  groupAppId: string;
  appName: string;
  connectionName?: string | null;
  spaceName?: string | null;
  status: SyncStatus;
  remote: string | null;
}

export function GroupItemSyncStatus({
  entries,
  isNew,
}: {
  entries: AppSyncEntry[];
  isNew: boolean;
}) {
  const { t } = useTranslation("sync-groups");

  return (
    <section className="mt-6 border-t border-hairline pt-4" aria-labelledby="group-item-sync-title">
      <h3
        id="group-item-sync-title"
        className="mb-3 font-mono text-[10px] font-normal uppercase tracking-widest text-faint"
      >
        {t("editor.perAppStatus")}
      </h3>

      {isNew ? (
        <p className="text-[13px] text-faint">{t("editor.newItemSyncHint")}</p>
      ) : entries.length === 0 ? (
        <p className="text-[13px] text-faint">{t("syncStatus.noApps")}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {entries.map((entry) => (
            <li key={entry.groupAppId} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                  {entry.appName}
                </span>
                <StatusBadge tone={SYNC_TONE[entry.status]} className="shrink-0">
                  {t(`syncStatus.${statusKey(entry.status)}`)}
                </StatusBadge>
              </div>
              {(entry.connectionName || entry.spaceName) && (
                <p className="truncate text-[11px] text-faint">
                  {[entry.connectionName, entry.spaceName].filter(Boolean).join(" · ")}
                </p>
              )}
              {entry.status === "out_of_sync" && entry.remote != null && (
                <p className="truncate font-mono text-[11px] text-faint">
                  {t("editor.remoteLabel")} {entry.remote}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
